import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Info, X, Lightbulb, Send } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';

declare const __APP_VERSION__: string | undefined;
declare const __BUILD_TIME__: string | undefined;
declare const __GIT_COMMIT__: string | undefined;
declare const __DEPLOY_ENV__: string | undefined;

const VersionDisplay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [featureRequest, setFeatureRequest] = useState({
    title: '',
    description: '',
    email: '',
    priority: 'medium'
  });

  const version =
    __APP_VERSION__ ??
    import.meta.env.VITE_APP_VERSION ??
    'dev';

  const buildTimestamp =
    __BUILD_TIME__ ??
    import.meta.env.VITE_BUILD_TIMESTAMP ??
    new Date().toISOString();

  const buildDate = formatDateTime(buildTimestamp, 'N/A', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const gitCommit = (__GIT_COMMIT__ ?? import.meta.env.VITE_GIT_COMMIT ?? '').trim();
  const gitShort = gitCommit ? gitCommit.slice(0, 7) : '';
  const deployEnv = (
    __DEPLOY_ENV__ ??
    import.meta.env.VITE_DEPLOY_ENV ??
    import.meta.env.MODE ??
    ''
  )
    .toString()
    .toUpperCase();
  const environmentLabel = deployEnv || 'LOCAL';

  const commitMarkup = gitShort
    ? `<div class="version-meta">Commit: ${gitShort}</div>`
    : '';
  const envMarkup = environmentLabel
    ? `<div class="version-meta">Environment: ${environmentLabel}</div>`
    : '';
  const tooltipParts = [
    `Built ${buildDate}`,
    gitShort && `commit ${gitShort}`,
    environmentLabel && environmentLabel !== 'LOCAL' ? environmentLabel : null,
  ]
    .filter(Boolean)
    .join(' • ');

  // Prevent body scroll when modal is open (SSR-safe)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (isOpen || showFeatureForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, showFeatureForm]);

  const handleFeatureSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would send to your backend
    console.log('Feature Request:', featureRequest);
    alert('Thank you for your feature request! We\'ll review it and get back to you.');
    setFeatureRequest({ title: '', description: '', email: '', priority: 'medium' });
    setShowFeatureForm(false);
  };

  const openVersionWindow = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const versionHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ChurchHub - Version Information</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: 20px;
      color: #333;
    }
    .container {
      max-width: 560px;
      margin: 0 auto;
      background: white;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      overflow: hidden;
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 24px;
      text-align: center;
    }
    .header h1 {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .version-badge {
      display: inline-block;
      background: rgba(255,255,255,0.2);
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 18px;
      font-weight: 600;
    }
    .version-date {
      font-size: 13px;
      opacity: 0.9;
      margin-top: 4px;
    }
    .version-meta {
      font-size: 12px;
      opacity: 0.85;
      margin-top: 2px;
    }
    .content {
      padding: 24px;
      max-height: 500px;
      overflow-y: auto;
    }
    .current-version {
      background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
      border: 2px solid #667eea;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 24px;
    }
    .current-version h2 {
      color: #667eea;
      font-size: 18px;
      margin-bottom: 12px;
    }
    .feature-list {
      display: grid;
      gap: 12px;
      margin-top: 12px;
    }
    .feature-item {
      display: flex;
      align-items: start;
      gap: 12px;
      padding: 12px;
      background: #f8f9fa;
      border-radius: 8px;
      border-left: 3px solid #667eea;
    }
    .feature-badge {
      background: #667eea;
      color: white;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
    }
    .feature-text {
      font-size: 14px;
      color: #555;
      line-height: 1.5;
    }
    .version-history {
      margin-top: 24px;
    }
    .version-history h3 {
      font-size: 18px;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e0e0e0;
    }
    .history-item {
      padding: 16px;
      background: #f8f9fa;
      border-radius: 8px;
      margin-bottom: 12px;
      border-left: 4px solid #764ba2;
    }
    .history-version {
      font-weight: 600;
      color: #764ba2;
      margin-bottom: 4px;
    }
    .history-title {
      font-weight: 600;
      margin-bottom: 4px;
      color: #333;
    }
    .history-desc {
      font-size: 13px;
      color: #666;
      line-height: 1.5;
    }
    .load-more {
      text-align: center;
      padding: 16px 0;
    }
    .load-more-btn {
      background: transparent;
      border: 2px solid #667eea;
      color: #667eea;
      padding: 8px 24px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }
    .load-more-btn:hover {
      background: #667eea;
      color: white;
    }
    .hidden {
      display: none;
    }
    .footer {
      padding: 20px 24px;
      background: #f8f9fa;
      border-top: 1px solid #e0e0e0;
      text-align: center;
    }
    .btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      padding: 12px 32px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s;
    }
    .btn:hover {
      transform: translateY(-2px);
    }
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #f1f1f1; }
    ::-webkit-scrollbar-thumb { background: #667eea; border-radius: 4px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>ChurchHub Version Information</h1>
      <div class="version-badge">v${version}</div>
      <div class="version-date">Last Updated: ${buildDate}</div>
      ${envMarkup}
      ${commitMarkup}
    </div>
    
    <div class="content">
      <div class="current-version">
        <h2>What's New in v${version}</h2>
        <div class="feature-list">
          <div class="feature-item">
            <span class="feature-badge">NEW</span>
            <span class="feature-text">Version dates and "Load More" functionality added</span>
          </div>
          <div class="feature-item">
            <span class="feature-badge">UX</span>
            <span class="feature-text">Cleaner version history with expandable older versions</span>
          </div>
          <div class="feature-item">
            <span class="feature-badge">FIXED</span>
            <span class="feature-text">Popup window displays perfectly without modal issues</span>
          </div>
        </div>
      </div>

      <div class="version-history">
        <h3>Recent Updates</h3>
        
        <div class="history-item">
          <div class="history-version">v1.8.18</div>
          <div class="history-title">Collapsible Modal Design</div>
          <div class="history-desc">Attempted collapsible version entries with improved UX</div>
        </div>

        <div class="history-item">
          <div class="history-version">v1.8.17</div>
          <div class="history-title">Mapbox Re-enabled</div>
          <div class="history-desc">Re-enabled Mapbox GL JS with proper token configuration</div>
        </div>

        <div class="history-item">
          <div class="history-version">v1.8.0 - v1.8.16</div>
          <div class="history-title">Modal & Map Fixes</div>
          <div class="history-desc">Multiple iterations fixing modal centering, scrolling, and Maplibre/Mapbox integration</div>
        </div>

        <!-- Hidden by default -->
        <div id="older-versions" class="hidden">
          <div class="history-item">
            <div class="history-version">v1.7.0</div>
            <div class="history-title">Enhanced Church Map</div>
            <div class="history-desc">Added Google Business Profile integration with photos and links</div>
          </div>

          <div class="history-item">
            <div class="history-version">v1.6.0</div>
            <div class="history-title">Business Directory & Network Map</div>
            <div class="history-desc">Added Community Business Directory and Church Hub Network Map visibility controls</div>
          </div>

          <div class="history-item">
            <div class="history-version">v1.5.0</div>
            <div class="history-title">WebGL Compatibility</div>
            <div class="history-desc">Enhanced WebGL error handling and compatibility testing</div>
          </div>

          <div class="history-item">
            <div class="history-version">v1.4.0</div>
            <div class="history-title">Mobile Navigation</div>
            <div class="history-desc">Professional mobile menu with improved styling and functionality</div>
          </div>

          <div class="history-item">
            <div class="history-version">v1.3.0</div>
            <div class="history-title">Pricing Updates</div>
            <div class="history-desc">New single-plan pricing with animated monthly/yearly toggle</div>
          </div>

          <div class="history-item">
            <div class="history-version">v1.0.0 - v1.2.0</div>
            <div class="history-title">Foundation & Marketing Site</div>
            <div class="history-desc">Initial release with professional marketing site, version tracking, and core features</div>
          </div>
        </div>

        <div class="load-more">
          <button class="load-more-btn" onclick="document.getElementById('older-versions').classList.toggle('hidden'); this.innerText = this.innerText === 'Load More Versions' ? 'Show Less' : 'Load More Versions';">
            Load More Versions
          </button>
        </div>
      </div>
    </div>

    <div class="footer">
      <button class="btn" onclick="window.close()">Close Window</button>
    </div>
  </div>
</body>
</html>
    `;

    const popup = window.open('', 'ChurchHub Version Info', 
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
    
    if (popup) {
      popup.document.write(versionHTML);
      popup.document.close();
    } else {
      alert('Please allow popups for this site to view version information.');
    }
  };

  if (isDismissed) return null;

  return (
    <>
      {/* Version Badge - In footer, not fixed */}
      <div className="flex items-center gap-2">
        <button
          onClick={openVersionWindow}
          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-gray-100 flex items-center gap-1"
          title={tooltipParts || undefined}
        >
          <Info className="h-3 w-3" />
          <span className="font-medium">v{version}</span>
          {gitShort && (
            <span className="font-mono text-[10px] text-muted-foreground">
              {gitShort}
            </span>
          )}
          {environmentLabel && (
            <Badge variant="outline" className="text-[9px] uppercase">
              {environmentLabel}
            </Badge>
          )}
        </button>
      </div>

      {/* Feature Request Form Popup */}
      {showFeatureForm && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div 
            className="relative w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: '90vh', height: 'auto' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-blue-500 text-white rounded-t-2xl flex-shrink-0">
              <h2 className="text-lg font-semibold">Suggest a Feature</h2>
              <button
                onClick={() => setShowFeatureForm(false)}
                className="text-white hover:text-gray-200 text-xl font-bold"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <form onSubmit={handleFeatureSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Feature Title</Label>
                  <Input
                    id="title"
                    value={featureRequest.title}
                    onChange={(e) => setFeatureRequest({...featureRequest, title: e.target.value})}
                    placeholder="Brief title for your feature request"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={featureRequest.description}
                    onChange={(e) => setFeatureRequest({...featureRequest, description: e.target.value})}
                    placeholder="Describe the feature you'd like to see. How would it help your church?"
                    rows={4}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    value={featureRequest.email}
                    onChange={(e) => setFeatureRequest({...featureRequest, email: e.target.value})}
                    placeholder="your.email@church.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    We'll use this to follow up on your suggestion
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <select
                    id="priority"
                    value={featureRequest.priority}
                    onChange={(e) => setFeatureRequest({...featureRequest, priority: e.target.value})}
                    className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="low">Low - Nice to have</option>
                    <option value="medium">Medium - Would be helpful</option>
                    <option value="high">High - Really need this</option>
                    <option value="critical">Critical - Essential for our church</option>
                  </select>
                </div>
              </form>
            </div>
            
            <div className="px-4 py-3 border-t border-slate-200 bg-gray-50 rounded-b-2xl flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowFeatureForm(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFeatureSubmit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VersionDisplay;